/**
 * COLLECTION BULK ACTIONS BAR
 *
 * Fixed bottom bar that appears when selection mode is active.
 * Allows bulk operations on selected items.
 * Extracted from Collection.tsx to reduce JSX tree complexity.
 */

import { X, Package, DollarSign, RefreshCw, Download, GitMerge, Archive, Trash2, Tag, Layers } from 'lucide-react';
import { AppIcons } from '../../constants/icons';
import { CategoryColors } from '../../constants/categoryColors';
import type { CollectionItem } from '../../types';

interface CollectionBulkActionsProps {
  isSelectionMode: boolean;
  selectedItems: Set<string>;
  setIsSelectionMode: (value: boolean) => void;
  setSelectedItems: (value: Set<string>) => void;
  paginatedItems: CollectionItem[];
  sortedItems: CollectionItem[];
  onSelectAllPage: () => void;
  onSelectAllFiltered: () => void;
  onBulkToggle: (field: 'isFavorite' | 'isRareLE' | 'isForDestash' | 'isOpenToSwaps' | 'onWishlist' | 'needsInfo' | 'swatched', value: boolean) => void;
  onBulkStorage: () => void;
  onBulkPrice: () => void;
  onBulkLine: () => void;
  onBulkCategory: () => void;
  onShowChangeTypeModal: () => void;
  onBulkExportCSV: () => void;
  onShowMergeModal: () => void;
  onBulkDestash: () => void;
  onBulkDelete: () => void;
}

export default function CollectionBulkActions({
  isSelectionMode,
  selectedItems,
  setIsSelectionMode,
  setSelectedItems,
  paginatedItems,
  sortedItems,
  onSelectAllPage,
  onSelectAllFiltered,
  onBulkToggle,
  onBulkStorage,
  onBulkPrice,
  onBulkLine,
  onBulkCategory,
  onShowChangeTypeModal,
  onBulkExportCSV,
  onShowMergeModal,
  onBulkDestash,
  onBulkDelete,
}: CollectionBulkActionsProps) {
  if (!isSelectionMode) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-glass-dark border-t-4 border-purple-600 shadow-2xl z-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header with count, selection buttons, and exit button */}
        <div className="flex flex-col gap-3 mb-4">
          {/* Top row: Count and Exit */}
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-glass font-body">
              {selectedItems.size} item(s) selected
            </span>
            <button
              onClick={() => {
                setIsSelectionMode(false);
                setSelectedItems(new Set());
              }}
              className="px-4 py-2 text-sm bg-slate-600/80 text-white rounded-lg hover:bg-slate-600 transition-default font-bold font-body"
            >
              Exit Selection Mode
            </button>
          </div>

          {/* Selection row: Quick select buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-glass-secondary font-body mr-1">Quick select:</span>
            <button
              onClick={onSelectAllPage}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-default font-body ${
                selectedItems.size === paginatedItems.length && paginatedItems.length > 0
                  ? 'bg-blue-500 text-white'
                  : 'bg-blue-600/80 text-white hover:bg-blue-600'
              }`}
            >
              {selectedItems.size === paginatedItems.length && paginatedItems.length > 0
                ? `✓ Page (${paginatedItems.length})`
                : `Select All on Page (${paginatedItems.length})`}
            </button>
            <button
              onClick={onSelectAllFiltered}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-default font-body ${
                selectedItems.size === sortedItems.length
                  ? 'bg-purple-500 text-white'
                  : 'bg-purple-600/80 text-white hover:bg-purple-600'
              }`}
            >
              {selectedItems.size === sortedItems.length
                ? `✓ All Filtered (${sortedItems.length})`
                : `Select All Filtered (${sortedItems.length})`}
            </button>
            {selectedItems.size > 0 && (
              <button
                onClick={() => setSelectedItems(new Set())}
                className="px-3 py-1.5 text-xs bg-slate-600/80 text-white rounded-lg hover:bg-slate-600 transition-default flex items-center gap-1 font-body"
              >
                <X className="icon-xs" /> Clear Selection
              </button>
            )}
          </div>
        </div>

        {/* Bulk Action Buttons - Responsive grid layout */}
        <div className="space-y-3">
          {/* Quick Flags - 2x2 grid on mobile, flex row on larger */}
          <div>
            <span className="text-sm text-glass-secondary font-medium mb-2 block font-body">Quick flags:</span>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              {/* Favorite - Heart */}
              <div className="flex rounded overflow-hidden border border-pink-300">
                <button
                  onClick={() => onBulkToggle('isFavorite', true)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-pink-500 text-white text-xs sm:text-sm font-medium hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Add to Favorites"
                >
                  <AppIcons.Favorite className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> +
                </button>
                <button
                  onClick={() => onBulkToggle('isFavorite', false)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-pink-100 text-pink-700 text-xs sm:text-sm font-medium hover:bg-pink-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Remove from Favorites"
                >
                  <AppIcons.Favorite className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> −
                </button>
              </div>

              {/* Wishlist - Star */}
              <div className="flex rounded overflow-hidden border border-purple-300">
                <button
                  onClick={() => onBulkToggle('onWishlist', true)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-purple-500 text-white text-xs sm:text-sm font-medium hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Add to Wishlist"
                >
                  <AppIcons.Wishlist className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> +
                </button>
                <button
                  onClick={() => onBulkToggle('onWishlist', false)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-purple-100 text-purple-700 text-xs sm:text-sm font-medium hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Remove from Wishlist"
                >
                  <AppIcons.Wishlist className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> −
                </button>
              </div>

              {/* Rare/LE - Gem */}
              <div className="flex rounded overflow-hidden border border-blue-300">
                <button
                  onClick={() => onBulkToggle('isRareLE', true)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-blue-500 text-white text-xs sm:text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Mark as Rare/LE"
                >
                  <AppIcons.RareLE className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> +
                </button>
                <button
                  onClick={() => onBulkToggle('isRareLE', false)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-blue-100 text-blue-700 text-xs sm:text-sm font-medium hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Remove Rare/LE"
                >
                  <AppIcons.RareLE className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> −
                </button>
              </div>

              {/* Needs Info - Info */}
              <div className="flex rounded overflow-hidden border border-cyan-300">
                <button
                  onClick={() => onBulkToggle('needsInfo', true)}
                  disabled={selectedItems.size === 0}
                  className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 ${CategoryColors.powder.badge} text-xs sm:text-sm font-medium ${CategoryColors.powder.bgHover} disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body`}
                  title="Mark Needs Info"
                >
                  <AppIcons.NeedsInfo className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> +
                </button>
                <button
                  onClick={() => onBulkToggle('needsInfo', false)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-cyan-100 text-cyan-700 text-xs sm:text-sm font-medium hover:bg-cyan-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Remove Needs Info"
                >
                  <AppIcons.NeedsInfo className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> −
                </button>
              </div>

              {/* Swatched - Palette */}
              <div className="flex rounded overflow-hidden border border-amber-300">
                <button
                  onClick={() => onBulkToggle('swatched', true)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-amber-500 text-white text-xs sm:text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Mark as Swatched"
                >
                  🎨 +
                </button>
                <button
                  onClick={() => onBulkToggle('swatched', false)}
                  disabled={selectedItems.size === 0}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 bg-amber-100 text-amber-700 text-xs sm:text-sm font-medium hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
                  title="Mark as Not Swatched"
                >
                  🎨 −
                </button>
              </div>
            </div>
          </div>

          {/* Actions - Grid on mobile */}
          <div className="pt-3 border-t border-slate-600">
            <span className="text-sm text-glass-secondary font-medium mb-2 block font-body">Actions:</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap gap-2">
              <button
                onClick={onBulkStorage}
                disabled={selectedItems.size === 0}
                className={`px-2 sm:px-3 py-1.5 ${CategoryColors.plate.badge} rounded text-xs sm:text-sm font-medium ${CategoryColors.plate.bgHover} disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body`}
              >
                <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Storage
              </button>

              <button
                onClick={onBulkPrice}
                disabled={selectedItems.size === 0}
                className="px-2 sm:px-3 py-1.5 bg-green-100 text-green-800 rounded text-xs sm:text-sm font-medium hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
              >
                <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Price
              </button>

              <button
                onClick={onBulkLine}
                disabled={selectedItems.size === 0}
                className="px-2 sm:px-3 py-1.5 bg-violet-100 text-violet-800 rounded text-xs sm:text-sm font-medium hover:bg-violet-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
              >
                <Tag className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Line
              </button>

              <button
                onClick={onBulkCategory}
                disabled={selectedItems.size === 0}
                className="px-2 sm:px-3 py-1.5 bg-teal-100 text-teal-800 rounded text-xs sm:text-sm font-medium hover:bg-teal-200 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
              >
                <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Category
              </button>

              <button
                onClick={onShowChangeTypeModal}
                disabled={selectedItems.size === 0}
                className={`px-2 sm:px-3 py-1.5 ${CategoryColors.embellishment.badge} rounded text-xs sm:text-sm font-medium ${CategoryColors.embellishment.bgHover} disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body`}
              >
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Type
              </button>

              <button
                onClick={onBulkExportCSV}
                disabled={selectedItems.size === 0}
                className={`px-2 sm:px-3 py-1.5 ${CategoryColors.transfer.badge} rounded text-xs sm:text-sm font-medium ${CategoryColors.transfer.bgHover} disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body`}
              >
                <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Export
              </button>

              <button
                onClick={onShowMergeModal}
                disabled={selectedItems.size < 2}
                className="px-2 sm:px-3 py-1.5 bg-slate-700 text-white rounded text-xs sm:text-sm font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
              >
                <GitMerge className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Merge
              </button>

              <button
                onClick={onBulkDestash}
                disabled={selectedItems.size === 0}
                className="px-2 sm:px-3 py-1.5 bg-gray-500 text-white rounded text-xs sm:text-sm font-medium hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
              >
                <Archive className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Archive
              </button>

              <button
                onClick={onBulkDelete}
                disabled={selectedItems.size === 0}
                className="px-2 sm:px-3 py-1.5 bg-red-600 text-white rounded text-xs sm:text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-default flex items-center justify-center gap-1 font-body"
              >
                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
